import { Box, styled, Typography } from '@mui/material';
import {
  selectConversations,
  selectLoadingConversations,
  selectLoadedConversations,
  fetchConversationsAsync,
  selectSelectedConversationID,
  sendMessageAsync,
  createConversationAsync,
  selectConversationMessages,
  selectConversationResponseError,
  selectConversationCreationError,
} from '../../../data/ai-services.slice';
import { useEffect } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { Conversations } from './Conversations';
import { Messages } from './Messages';
import { MessageInput } from './MessageInput';
import { useForm } from 'react-hook-form';

const PREFIX = 'AiServicesPage';

const componentClasses = {
  wrapper: `${PREFIX}-wrapper`,
  conversationList: `${PREFIX}-conversation-list`,
  conversationCurrent: `${PREFIX}-conversation-current`,
  messages: `${PREFIX}-messages`,
  messageInput: `${PREFIX}-message-input`,
  messageInputAnchored: `${PREFIX}-message-input-anchored`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${componentClasses.wrapper}`]: {
    padding: 0,
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    height: '100%',
  },
  [`& .${componentClasses.conversationList}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    // border: `1px dotted ${theme.palette.divider}`,
  },
  [`& .${componentClasses.conversationCurrent}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    flex: 1,
    // border: `1px dotted ${theme.palette.divider}`,
    position: 'relative',
  },
  [`& .${componentClasses.messages}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingBottom: '70px',
    overflowY: 'auto',
    // border: `1px dotted ${theme.palette.divider}`,
  },
  [`& .${componentClasses.messageInput}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    // border: `1px dotted ${theme.palette.divider}`,
  },
  [`& .${componentClasses.messageInputAnchored}`]: {
    position: 'absolute',
    bottom: 0,
    left: 15,
    right: 15,
    zIndex: 1000,
    backgroundColor: '#fff',
  },
}));

const AiServicesPage = () => {
  const dispatch = useAppDispatch();
  const defaultValues = {
    question: '',
  };
  const {
    formState: { isDirty },
    control,
    reset,
    getValues,
    handleSubmit,
  } = useForm({
    mode: 'onTouched',
    defaultValues,
  });
  const selectedConversationID = useAppSelector(selectSelectedConversationID);
  const conversationMessages = useAppSelector(selectConversationMessages);
  const conversationCreationError = useAppSelector(
    selectConversationCreationError,
  );
  const conversationResponseError = useAppSelector(
    selectConversationResponseError,
  );

  const messageInputClass =
    conversationMessages.length > 0
      ? componentClasses.messageInputAnchored
      : componentClasses.messageInput;

  const handleMessageSubmit = (data: any) => {
    if (isDirty) {
      const values = getValues();
      const query = {
        question: values.question,
      };
      if (selectedConversationID) {
        dispatch(sendMessageAsync(selectedConversationID, query));
      } else {
        dispatch(createConversationAsync(query));
      }
      reset(defaultValues);
    }
  };

  useEffect(() => {
    reset(defaultValues);
  }, [selectedConversationID]);

  useEffect(() => {
    if (conversationCreationError) {
      console.error(conversationCreationError);
    }
  }, [conversationCreationError]);

  useEffect(() => {
    if (conversationResponseError) {
      console.error(conversationResponseError);
    }
  }, [conversationResponseError]);

  return (
    <StyledBox className={componentClasses.wrapper}>
      <Box className={componentClasses.conversationList}>
        <Conversations />
      </Box>
      <Box className={componentClasses.conversationCurrent}>
        <Box className={componentClasses.messages}>
          <Messages />
        </Box>
        <Box className={messageInputClass}>
          <MessageInput
            control={control}
            onSubmit={handleSubmit(handleMessageSubmit)}
          />
        </Box>
      </Box>
    </StyledBox>
  );
};

export { AiServicesPage };
